import { useEffect, useState } from 'react';
import { create } from 'zustand';

type Kind = 'ok' | 'info';
export interface HudAction { label: string; run: () => void }
interface HudState { msg: string; kind: Kind; seq: number; action: HudAction | null }
const useHudStore = create<HudState>(() => ({ msg: '', kind: 'info', seq: 0, action: null }));

const OK = /finaliz|criad|adicionad|salv|importad|fixad|movida|aprovad|desfeit/i;
/** Com "Desfazer", o aviso fica mais tempo na tela. */
export const UNDO_MS = 6000;

/** Aviso rápido no rodapé ("Tarefa finalizada", erros de rede…), opcionalmente com uma ação ("Desfazer"). */
export function hud(msg: string, opts?: Kind | { kind?: Kind; action?: HudAction }) {
  const o = typeof opts === 'string' ? { kind: opts } : opts ?? {};
  useHudStore.setState(s => ({ msg, kind: o.kind ?? (OK.test(msg) ? 'ok' : 'info'), seq: s.seq + 1, action: o.action ?? null }));
}

/** Executa a ação do aviso visível (botão ou Ctrl+Z) uma vez só. */
function runAction() {
  const a = useHudStore.getState().action;
  if (!a) return false;
  useHudStore.setState({ action: null });
  a.run();
  return true;
}

export function Hud() {
  const { msg, kind, seq, action } = useHudStore();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!seq) return;
    setShow(true);
    const t = setTimeout(() => { setShow(false); useHudStore.setState({ action: null }); }, action ? UNDO_MS : 2600);
    return () => clearTimeout(t);
  // A ação muda junto com seq; o tempo é decidido na hora em que o aviso aparece.
  }, [seq]);
  // Ctrl+Z (⌘Z) desfaz enquanto o aviso com "Desfazer" está na tela, fora de campos de texto.
  useEffect(() => {
    if (!action) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.key.toLowerCase() !== 'z') return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (/INPUT|TEXTAREA|SELECT/.test(el.tagName) || el.isContentEditable)) return;
      if (runAction()) e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [action]);
  return (
    <div id="hud" role="status" aria-live="polite" className={`${show ? 'show' : ''} ${action ? 'has-action' : ''}`} key={seq}>
      <span className={`hud-ic ${kind}`}>
        {kind === 'ok'
          ? <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" pathLength={1} /></svg>
          : <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M8 4.5v4.5M8 11.6v.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>}
      </span>
      <span>{msg}</span>
      {action && show && <button type="button" className="hud-act" onClick={runAction}>{action.label}</button>}
    </div>
  );
}
